import type { StreamEvent } from '@/types/message'

type Options = {
  reducedMotion?: boolean
  schedule?: (callback: FrameRequestCallback) => number
  cancel?: (id: number) => void
  now?: () => number
}

/** Reveal verified text smoothly; completion waits for the visible text. */
export function createStreamPresenter(emit: (event: StreamEvent) => void, options: Options = {}) {
  const schedule = options.schedule ?? ((callback) => requestAnimationFrame(callback))
  const cancelFrame = options.cancel ?? ((id) => cancelAnimationFrame(id))
  const now = options.now ?? (() => performance.now())
  let text = ''
  let visible = 0
  let started: number | null = null
  let frame: number | null = null
  let completion: StreamEvent | null = null
  let cancelled = false

  function finish() {
    if (completion && visible === text.length) {
      const event = completion
      completion = null
      emit(event)
    }
  }

  function reveal(timestamp: number) {
    frame = null
    if (cancelled) return
    // Short replies feel immediate; long replies finish revealing within 1.8s.
    const rate = Math.max(0.6, text.length / 1800)
    let end = Math.min(text.length, Math.max(visible + 1, Math.floor((timestamp - (started ?? timestamp)) * rate)))
    // Never show half of an emoji or another UTF-16 surrogate pair.
    if (end < text.length && /[\uD800-\uDBFF]/.test(text[end - 1] ?? '')) end++
    if (end > visible) {
      const token = text.slice(visible, end)
      visible = end
      emit({ type: 'token', token })
    }
    if (visible < text.length) frame = schedule(reveal)
    else finish()
  }

  function cancel() {
    cancelled = true
    if (frame !== null) cancelFrame(frame)
    frame = null
    completion = null
  }

  return {
    cancel,
    push(event: StreamEvent) {
      if (cancelled) return
      if (options.reducedMotion) {
        emit(event)
      } else if (event.type === 'token' && event.token) {
        text += event.token
        if (started === null) started = now()
        if (frame === null) frame = schedule(reveal)
      } else if (event.type === 'done') {
        completion = event
        finish()
      } else if (event.type === 'error' || event.type === 'human_takeover') {
        cancel()
        emit(event)
      } else {
        // Acknowledgments and source metadata must never wait for animation.
        emit(event)
      }
    },
  }
}
